import { trackCalendlyBooked, trackEvent } from "./analytics";

type CalendlyEventPayload = {
  event: {
    uri: string;
    start_time: string;
    start_time_pretty: string;
    invitees_counter: {
      total: number;
    };
  };
  event_type: {
    name: string;
    uri: string;
  };
  invitee: {
    email: string;
    name: string;
    uri: string;
    timezone: string;
  };
};

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget(options: { url: string }): void;
      closePopupWidget(): void;
    };
  }
}

const CALENDLY_URL = "https://calendly.com/contact-rugira/30min?hide_event_type_details=1&hide_landing_page_details=1";
const CALENDLY_SCRIPT_ID = "calendly-widget-script";

let calendlyScriptPromise: Promise<void> | null = null;
let hasRegisteredListener = false;
let lastOpenContext: { section: string } | null = null;

const loadCalendlyScript = (): Promise<void> => {
  if (calendlyScriptPromise) {
    return calendlyScriptPromise;
  }

  calendlyScriptPromise = new Promise<void>((resolve, reject) => {
    if (document.getElementById(CALENDLY_SCRIPT_ID)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = CALENDLY_SCRIPT_ID;
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Calendly widget"));
    document.body.appendChild(script);
  });

  return calendlyScriptPromise;
};

const registerCalendlyListener = () => {
  if (hasRegisteredListener) return;

  window.addEventListener("message", (event) => {
    if (event.origin !== "https://calendly.com") {
      return;
    }

    const data = event.data;
    if (!data || typeof data !== "object") {
      return;
    }

    if (data.event === "calendly.event_scheduled") {
      const payload = data.payload as CalendlyEventPayload;
      const section = lastOpenContext?.section ?? "unknown";

      trackCalendlyBooked(section, {
        event_type: payload?.event_type?.name,
      });
    }
  });

  hasRegisteredListener = true;
};

export const openCalendlyBooking = async (section: string) => {
  lastOpenContext = { section };
  trackEvent("preview_call_opened", {
    event_category: "conversion_intent",
    event_label: "calendly",
    section,
  });

  try {
    registerCalendlyListener();
    await loadCalendlyScript();

    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      throw new Error("Calendly widget unavailable");
    }
  } catch (error) {
    console.error("Calendly booking failed to initialize:", error);
    trackEvent("preview_call_error", {
      event_category: "conversion_error",
      event_label: (error as Error).message,
      section,
    });
    window.open("https://calendly.com/contact-rugira/30min", "_blank", "noopener,noreferrer");
  }
};
